import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';

import { Icon, type IconName } from '@/components/ui/Icon';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonLookProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  /** Icon before the label. */
  icon?: IconName;
  /** Icon after the label. */
  trailingIcon?: IconName;
}

function buttonClassName(
  { variant, size, fullWidth }: Required<Pick<ButtonLookProps, 'variant' | 'size' | 'fullWidth'>>,
  className?: string,
): string {
  return cn(styles.button, styles[variant], styles[size], fullWidth && styles.fullWidth, className);
}

const ICON_SIZE: Record<ButtonSize, number> = { sm: 16, md: 18, lg: 20 };

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonLookProps {
  isLoading?: boolean;
  children: ReactNode;
}

/** One primary action per view; secondary for the rest, ghost for low-emphasis, danger to destroy. */
export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  trailingIcon,
  isLoading = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClassName({ variant, size, fullWidth }, className)}
      disabled={disabled ?? isLoading}
      aria-busy={isLoading}
      {...rest}
    >
      {isLoading ? (
        <Spinner size="sm" label="" />
      ) : (
        icon && <Icon name={icon} size={ICON_SIZE[size]} />
      )}
      {children}
      {trailingIcon && <Icon name={trailingIcon} size={ICON_SIZE[size]} />}
    </button>
  );
}

export interface LinkButtonProps extends LinkProps, ButtonLookProps {
  children: ReactNode;
}

/** A router link that looks like a button — for navigation, never for actions. */
export function LinkButton({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  trailingIcon,
  className,
  children,
  ...rest
}: LinkButtonProps) {
  return (
    <Link className={buttonClassName({ variant, size, fullWidth }, className)} {...rest}>
      {icon && <Icon name={icon} size={ICON_SIZE[size]} />}
      {children}
      {trailingIcon && <Icon name={trailingIcon} size={ICON_SIZE[size]} />}
    </Link>
  );
}
